import { BernieGraphqlClient, FetchPolicy, RequestOptions } from 'bernie-graphql';

import * as generated from './types';

import * as operations from './operations';

export interface OperationResult<T> {
  data: T;
  errors?: [];
  extensions: any;
}

function isFetchPolicy(requestOptions: RequestOptions | FetchPolicy): requestOptions is FetchPolicy {
  return typeof requestOptions === 'string';
}

export class ApiOperations {
  public constructor(private client: BernieGraphqlClient) {
    //
  }

  public addPropertyToCart(
    variables: generated.AddPropertyToCart.Variables,
    options?: FetchPolicy | RequestOptions
  ): Promise<OperationResult<generated.AddPropertyToCart.Mutation>> {
    let requestOptions: RequestOptions;
    let fetchPolicy: FetchPolicy;

    if (options) {
      requestOptions = isFetchPolicy(options) ? null : options;
      fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
    }

    return this.client
      .mutate(
        {
          mutation: operations.addPropertyToCartQuery,
          variables,
          fetchPolicy
        },
        requestOptions
      )
      .then(result => {
        const data = result.data as generated.AddPropertyToCart.Mutation;
        const extensions = result.extensions;
        const errors = result.errors;

        return { data, errors, extensions };
      });
  }

  public createQuestion(
    variables: generated.CreateQuestion.Variables,
    options?: FetchPolicy | RequestOptions
  ): Promise<OperationResult<generated.CreateQuestion.Mutation>> {
    let requestOptions: RequestOptions;
    let fetchPolicy: FetchPolicy;

    if (options) {
      requestOptions = isFetchPolicy(options) ? null : options;
      fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
    }

    return this.client
      .mutate(
        {
          mutation: operations.createQuestionQuery,
          variables,
          fetchPolicy
        },
        requestOptions
      )
      .then(result => {
        const data = result.data as generated.CreateQuestion.Mutation;
        const extensions = result.extensions;
        const errors = result.errors;

        return { data, errors, extensions };
      });
  }

  public createLodgingConversation(
    variables: generated.CreateLodgingConversation.Variables,
    options?: FetchPolicy | RequestOptions
  ): Promise<OperationResult<generated.CreateLodgingConversation.Mutation>> {
    let requestOptions: RequestOptions;
    let fetchPolicy: FetchPolicy;

    if (options) {
      requestOptions = isFetchPolicy(options) ? null : options;
      fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
    }

    return this.client
      .mutate(
        {
          mutation: operations.createLodgingConversationQuery,
          variables,
          fetchPolicy
        },
        requestOptions
      )
      .then(result => {
        const data = result.data as generated.CreateLodgingConversation.Mutation;
        const extensions = result.extensions;
        const errors = result.errors;

        return { data, errors, extensions };
      });
  }

  public createSessionCart(
    variables: generated.CreateSessionCart.Variables,
    options?: FetchPolicy | RequestOptions
  ): Promise<OperationResult<generated.CreateSessionCart.Mutation>> {
    let requestOptions: RequestOptions;
    let fetchPolicy: FetchPolicy;

    if (options) {
      requestOptions = isFetchPolicy(options) ? null : options;
      fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
    }

    return this.client
      .mutate(
        {
          mutation: operations.createSessionCartQuery,
          variables,
          fetchPolicy
        },
        requestOptions
      )
      .then(result => {
        const data = result.data as generated.CreateSessionCart.Mutation;
        const extensions = result.extensions;
        const errors = result.errors;

        return { data, errors, extensions };
      });
  }

  public customer(
    variables: generated.Customer.Variables,
    options?: FetchPolicy | RequestOptions
  ): Promise<OperationResult<generated.Customer.Query>> {
    let requestOptions: RequestOptions;
    let fetchPolicy: FetchPolicy;

    if (options) {
      requestOptions = isFetchPolicy(options) ? null : options;
      fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
    }

    return this.client
      .query(
        {
          query: operations.customerQuery,
          variables,
          fetchPolicy
        },
        requestOptions
      )
      .then(result => {
        const data = result.data as generated.Customer.Query;
        const extensions = result.extensions;
        const errors = result.errors;

        return { data, errors, extensions };
      });
  }

  public emptyCart(
    variables: generated.EmptyCart.Variables,
    options?: FetchPolicy | RequestOptions
  ): Promise<OperationResult<generated.EmptyCart.Mutation>> {
    let requestOptions: RequestOptions;
    let fetchPolicy: FetchPolicy;

    if (options) {
      requestOptions = isFetchPolicy(options) ? null : options;
      fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
    }

    return this.client
      .mutate(
        {
          mutation: operations.emptyCartQuery,
          variables,
          fetchPolicy
        },
        requestOptions
      )
      .then(result => {
        const data = result.data as generated.EmptyCart.Mutation;
        const extensions = result.extensions;
        const errors = result.errors;

        return { data, errors, extensions };
      });
  }

  public cart(variables: generated.Cart.Variables, options?: FetchPolicy | RequestOptions): Promise<OperationResult<generated.Cart.Query>> {
    let requestOptions: RequestOptions;
    let fetchPolicy: FetchPolicy;

    if (options) {
      requestOptions = isFetchPolicy(options) ? null : options;
      fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
    }

    return this.client
      .query(
        {
          query: operations.cartQuery,
          variables,
          fetchPolicy
        },
        requestOptions
      )
      .then(result => {
        const data = result.data as generated.Cart.Query;
        const extensions = result.extensions;
        const errors = result.errors;

        return { data, errors, extensions };
      });
  }

  public conversations(
    variables: generated.Conversations.Variables,
    options?: FetchPolicy | RequestOptions
  ): Promise<OperationResult<generated.Conversations.Query>> {
    let requestOptions: RequestOptions;
    let fetchPolicy: FetchPolicy;

    if (options) {
      requestOptions = isFetchPolicy(options) ? null : options;
      fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
    }

    return this.client
      .query(
        {
          query: operations.conversationsQuery,
          variables,
          fetchPolicy
        },
        requestOptions
      )
      .then(result => {
        const data = result.data as generated.Conversations.Query;
        const extensions = result.extensions;
        const errors = result.errors;

        return { data, errors, extensions };
      });
  }

  public findAnswersSearch(
    variables: generated.FindAnswersSearch.Variables,
    options?: FetchPolicy | RequestOptions
  ): Promise<OperationResult<generated.FindAnswersSearch.Query>> {
    let requestOptions: RequestOptions;
    let fetchPolicy: FetchPolicy;

    if (options) {
      requestOptions = isFetchPolicy(options) ? null : options;
      fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
    }

    return this.client
      .query(
        {
          query: operations.findAnswersSearchQuery,
          variables,
          fetchPolicy
        },
        requestOptions
      )
      .then(result => {
        const data = result.data as generated.FindAnswersSearch.Query;
        const extensions = result.extensions;
        const errors = result.errors;

        return { data, errors, extensions };
      });
  }

  public fullPropertySearch(
    variables: generated.FullPropertySearch.Variables,
    options?: FetchPolicy | RequestOptions
  ): Promise<OperationResult<generated.FullPropertySearch.Query>> {
    let requestOptions: RequestOptions;
    let fetchPolicy: FetchPolicy;

    if (options) {
      requestOptions = isFetchPolicy(options) ? null : options;
      fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
    }

    return this.client
      .query(
        {
          query: operations.fullPropertySearchQuery,
          variables,
          fetchPolicy
        },
        requestOptions
      )
      .then(result => {
        const data = result.data as generated.FullPropertySearch.Query;
        const extensions = result.extensions;
        const errors = result.errors;

        return { data, errors, extensions };
      });
  }

  public propertyLocationScoreInfo(
    variables: generated.PropertyLocationScoreInfo.Variables,
    options?: FetchPolicy | RequestOptions
  ): Promise<OperationResult<generated.PropertyLocationScoreInfo.Query>> {
    let requestOptions: RequestOptions;
    let fetchPolicy: FetchPolicy;

    if (options) {
      requestOptions = isFetchPolicy(options) ? null : options;
      fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
    }

    return this.client
      .query(
        {
          query: operations.propertyLocationScoreInfoQuery,
          variables,
          fetchPolicy
        },
        requestOptions
      )
      .then(result => {
        const data = result.data as generated.PropertyLocationScoreInfo.Query;
        const extensions = result.extensions;
        const errors = result.errors;

        return { data, errors, extensions };
      });
  }

  public prepareCheckoutByCartId(
    variables: generated.PrepareCheckoutByCartId.Variables,
    options?: FetchPolicy | RequestOptions
  ): Promise<OperationResult<generated.PrepareCheckoutByCartId.Mutation>> {
    let requestOptions: RequestOptions;
    let fetchPolicy: FetchPolicy;

    if (options) {
      requestOptions = isFetchPolicy(options) ? null : options;
      fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
    }

    return this.client
      .mutate(
        {
          mutation: operations.prepareCheckoutByCartIdQuery,
          variables,
          fetchPolicy
        },
        requestOptions
      )
      .then(result => {
        const data = result.data as generated.PrepareCheckoutByCartId.Mutation;
        const extensions = result.extensions;
        const errors = result.errors;

        return { data, errors, extensions };
      });
  }

  public prepareCheckout(
    variables: generated.PrepareCheckout.Variables,
    options?: FetchPolicy | RequestOptions
  ): Promise<OperationResult<generated.PrepareCheckout.Mutation>> {
    let requestOptions: RequestOptions;
    let fetchPolicy: FetchPolicy;

    if (options) {
      requestOptions = isFetchPolicy(options) ? null : options;
      fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
    }

    return this.client
      .mutate(
        {
          mutation: operations.prepareCheckoutQuery,
          variables,
          fetchPolicy
        },
        requestOptions
      )
      .then(result => {
        const data = result.data as generated.PrepareCheckout.Mutation;
        const extensions = result.extensions;
        const errors = result.errors;

        return { data, errors, extensions };
      });
  }

  public propertyRoomScoreInfo(
    variables: generated.PropertyRoomScoreInfo.Variables,
    options?: FetchPolicy | RequestOptions
  ): Promise<OperationResult<generated.PropertyRoomScoreInfo.Query>> {
    let requestOptions: RequestOptions;
    let fetchPolicy: FetchPolicy;

    if (options) {
      requestOptions = isFetchPolicy(options) ? null : options;
      fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
    }

    return this.client
      .query(
        {
          query: operations.propertyRoomScoreInfoQuery,
          variables,
          fetchPolicy
        },
        requestOptions
      )
      .then(result => {
        const data = result.data as generated.PropertyRoomScoreInfo.Query;
        const extensions = result.extensions;
        const errors = result.errors;

        return { data, errors, extensions };
      });
  }

  public propertyKeyPhrases(
    variables: generated.PropertyKeyPhrases.Variables,
    options?: FetchPolicy | RequestOptions
  ): Promise<OperationResult<generated.PropertyKeyPhrases.Query>> {
    let requestOptions: RequestOptions;
    let fetchPolicy: FetchPolicy;

    if (options) {
      requestOptions = isFetchPolicy(options) ? null : options;
      fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
    }

    return this.client
      .query(
        {
          query: operations.propertyKeyPhrasesQuery,
          variables,
          fetchPolicy
        },
        requestOptions
      )
      .then(result => {
        const data = result.data as generated.PropertyKeyPhrases.Query;
        const extensions = result.extensions;
        const errors = result.errors;

        return { data, errors, extensions };
      });
  }

  public propertyMapMigration(
    variables: generated.PropertyMapMigration.Variables,
    options?: FetchPolicy | RequestOptions
  ): Promise<OperationResult<generated.PropertyMapMigration.Query>> {
    let requestOptions: RequestOptions;
    let fetchPolicy: FetchPolicy;

    if (options) {
      requestOptions = isFetchPolicy(options) ? null : options;
      fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
    }

    return this.client
      .query(
        {
          query: operations.propertyMapMigrationQuery,
          variables,
          fetchPolicy
        },
        requestOptions
      )
      .then(result => {
        const data = result.data as generated.PropertyMapMigration.Query;
        const extensions = result.extensions;
        const errors = result.errors;

        return { data, errors, extensions };
      });
  }

  public propertySearch(
    variables: generated.PropertySearch.Variables,
    options?: FetchPolicy | RequestOptions
  ): Promise<OperationResult<generated.PropertySearch.Query>> {
    let requestOptions: RequestOptions;
    let fetchPolicy: FetchPolicy;

    if (options) {
      requestOptions = isFetchPolicy(options) ? null : options;
      fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
    }

    return this.client
      .query(
        {
          query: operations.propertySearchQuery,
          variables,
          fetchPolicy
        },
        requestOptions
      )
      .then(result => {
        const data = result.data as generated.PropertySearch.Query;
        const extensions = result.extensions;
        const errors = result.errors;

        return { data, errors, extensions };
      });
  }

  public removeItemFromCart(
    variables: generated.RemoveItemFromCart.Variables,
    options?: FetchPolicy | RequestOptions
  ): Promise<OperationResult<generated.RemoveItemFromCart.Mutation>> {
    let requestOptions: RequestOptions;
    let fetchPolicy: FetchPolicy;

    if (options) {
      requestOptions = isFetchPolicy(options) ? null : options;
      fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
    }

    return this.client
      .mutate(
        {
          mutation: operations.removeItemFromCartQuery,
          variables,
          fetchPolicy
        },
        requestOptions
      )
      .then(result => {
        const data = result.data as generated.RemoveItemFromCart.Mutation;
        const extensions = result.extensions;
        const errors = result.errors;

        return { data, errors, extensions };
      });
  }

  public translateReview(
    variables: generated.TranslateReview.Variables,
    options?: FetchPolicy | RequestOptions
  ): Promise<OperationResult<generated.TranslateReview.Query>> {
    let requestOptions: RequestOptions;
    let fetchPolicy: FetchPolicy;

    if (options) {
      requestOptions = isFetchPolicy(options) ? null : options;
      fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
    }

    return this.client
      .query(
        {
          query: operations.translateReviewQuery,
          variables,
          fetchPolicy
        },
        requestOptions
      )
      .then(result => {
        const data = result.data as generated.TranslateReview.Query;
        const extensions = result.extensions;
        const errors = result.errors;

        return { data, errors, extensions };
      });
  }

  public reviews(
    variables: generated.Reviews.Variables,
    options?: FetchPolicy | RequestOptions
  ): Promise<OperationResult<generated.Reviews.Query>> {
    let requestOptions: RequestOptions;
    let fetchPolicy: FetchPolicy;

    if (options) {
      requestOptions = isFetchPolicy(options) ? null : options;
      fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
    }

    return this.client
      .query(
        {
          query: operations.reviewsQuery,
          variables,
          fetchPolicy
        },
        requestOptions
      )
      .then(result => {
        const data = result.data as generated.Reviews.Query;
        const extensions = result.extensions;
        const errors = result.errors;

        return { data, errors, extensions };
      });
  }

  public questionAnswers(
    variables: generated.QuestionAnswers.Variables,
    options?: FetchPolicy | RequestOptions
  ): Promise<OperationResult<generated.QuestionAnswers.Query>> {
    let requestOptions: RequestOptions;
    let fetchPolicy: FetchPolicy;

    if (options) {
      requestOptions = isFetchPolicy(options) ? null : options;
      fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
    }

    return this.client
      .query(
        {
          query: operations.questionAnswersQuery,
          variables,
          fetchPolicy
        },
        requestOptions
      )
      .then(result => {
        const data = result.data as generated.QuestionAnswers.Query;
        const extensions = result.extensions;
        const errors = result.errors;

        return { data, errors, extensions };
      });
  }

  public upvotePropertyReview(
    variables: generated.UpvotePropertyReview.Variables,
    options?: FetchPolicy | RequestOptions
  ): Promise<OperationResult<generated.UpvotePropertyReview.Mutation>> {
    let requestOptions: RequestOptions;
    let fetchPolicy: FetchPolicy;

    if (options) {
      requestOptions = isFetchPolicy(options) ? null : options;
      fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
    }

    return this.client
      .mutate(
        {
          mutation: operations.upvotePropertyReviewQuery,
          variables,
          fetchPolicy
        },
        requestOptions
      )
      .then(result => {
        const data = result.data as generated.UpvotePropertyReview.Mutation;
        const extensions = result.extensions;
        const errors = result.errors;

        return { data, errors, extensions };
      });
  }

  public static withClient(client: BernieGraphqlClient) {
    return new ApiOperations(client);
  }
}
